import { routing } from '@/i18n/routing'
import { pushLogoutToDataLayer } from '@/lib/analytics/userIdentity'
import { useAuthStore } from '@/stores/authStore'
import {
  getAuthToken,
  getRefreshToken,
  isAuthenticated,
  isTokenExpired
} from '@/utils/auth'
import { clearTokens, refreshTokens } from '@/utils/refreshTokens'

import { MarketApi } from './generated-api'

export interface GatewayError {
  code: number
  message: string
}

export const getGatewayErrorMessage = (error: unknown) => {
  if (error && typeof error === 'object' && 'error' in error) {
    const err = error as { error: GatewayError }
    return err.error?.message
  }
  return undefined
}

export const getGatewayErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object' || !('error' in error)) {
    return undefined
  }
  const inner = (error as { error: unknown }).error
  if (!inner || typeof inner !== 'object' || !('code' in inner)) {
    return undefined
  }
  const code = (inner as { code: unknown }).code
  return typeof code === 'string' ? code : undefined
}

export interface CustomRequestTimestamp {
  date?: string
}

const isClient = typeof window !== 'undefined'

const normalizeToken = (token: string) =>
  token.replace(/^Bearer\s+/i, '').trim()

const setAuthorizationHeader = (headers: Headers, token: string) => {
  const normalizedToken = normalizeToken(token)
  if (normalizedToken) {
    headers.set('Authorization', `Bearer ${normalizedToken}`)
  }
}

const allowedLocales = new Set<string>(routing.locales)

const resolveApiLang = (raw: string | undefined): string => {
  const normalized = raw?.trim().toLowerCase() ?? ''
  if (allowedLocales.has(normalized)) {
    return normalized
  }
  return routing.defaultLocale
}

const getLocale = (): string => {
  if (typeof window !== 'undefined') {
    const cookies = document.cookie.split(';')
    const localeCookie = cookies.find(c => c.trim().startsWith('NEXT_LOCALE='))
    if (localeCookie) {
      return resolveApiLang(localeCookie.split('=')[1])
    }

    return resolveApiLang(navigator.language?.split('-')[0])
  }
  return routing.defaultLocale
}

export const apiInstance = { nextAuthAccessToken: '' }

let isRefreshing = false
let refreshPromise: Promise<string | undefined> | null = null

/**
 * api-gateway maps gRPC FailedPrecondition to Fiber errors with a plain-text
 * body (SendString). swagger-typescript-api always parses errors as JSON, so
 * codes like `concurrent_limit_reached` were lost and clients fell back to
 * generic messages. Re-wrap plain-text error bodies as JSON the client can read.
 */
export async function normalizePlainTextErrorResponse(
  response: Response
): Promise<Response> {
  if (response.ok) {
    return response
  }

  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    return response
  }

  const text = (await response.text()).trim()
  const body =
    text.startsWith('{') || text.startsWith('[') ? text : JSON.stringify(text)

  const headers = new Headers(response.headers)
  headers.set('Content-Type', 'application/json')

  return new Response(body, {
    status: response.status,
    statusText: response.statusText,
    headers
  })
}

const finalizeResponse = (response: Response) =>
  response.ok ? response : normalizePlainTextErrorResponse(response)

type SessionEndHandler = () => unknown

const sessionEndHandlers = new Set<SessionEndHandler>()

/** endSession waits for the handlers this long at most; the session is gone. */
const SESSION_END_TIMEOUT_MS = 2000

/**
 * Lets a feature clean up when the session dies without a logout (revoked or
 * expired refresh token) — the notifications feature unsubscribes the
 * browser from the account's pushes. Features register here so that this
 * module never imports them. Returns the unregister function.
 */
export function onSessionEnd(handler: SessionEndHandler): () => void {
  sessionEndHandlers.add(handler)
  return () => {
    sessionEndHandlers.delete(handler)
  }
}

const runSessionEndHandlers = async () => {
  let timer: ReturnType<typeof setTimeout> | undefined
  await Promise.race([
    Promise.allSettled(
      [...sessionEndHandlers].map(handler => Promise.resolve().then(handler))
    ),
    new Promise<void>(resolve => {
      timer = setTimeout(resolve, SESSION_END_TIMEOUT_MS)
    })
  ])
  clearTimeout(timer)
}

/**
 * The session is over. Every place that detects a dead session must come
 * through here: a store left saying "logged in" after the cookies are gone
 * renders a logged-in UI that sends no Authorization header and 401s on every
 * call, with nothing to tell the user why.
 */
const endSession = async () => {
  await runSessionEndHandlers()
  await clearTokens()
  pushLogoutToDataLayer('session_expired')
  useAuthStore.getState().logout()
  if (window.location.pathname !== '/') {
    window.location.href = '/'
  }
}

export const customFetch = async (
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> => {
  const headers = new Headers(init?.headers)
  const requestUrl = typeof input === 'string' ? input : input.toString()
  const isRefreshRequest = requestUrl.includes('/api/v2/auth/refresh')

  // A caller may pin the language of the page it renders (BAT-860: the
  // NEXT_LOCALE cookie can lag behind a language switch).
  if (!headers.has('Lang')) {
    headers.set('Lang', getLocale())
  }

  let authToken: string | undefined =
    getAuthToken() || apiInstance.nextAuthAccessToken || undefined
  const refreshToken = getRefreshToken()
  // Captured before the refresh below can wipe the cookies, so the 401 handler
  // can tell a guest who never had a session from one that just died.
  const startedWithSession = Boolean(authToken || refreshToken)

  if (!authToken && isClient && !isRefreshRequest && refreshToken) {
    if (!isRefreshing) {
      isRefreshing = true
      refreshPromise = refreshTokens().finally(() => {
        isRefreshing = false
        refreshPromise = null
      })
    }
    const pendingRefresh = refreshPromise
    if (pendingRefresh) {
      authToken = await pendingRefresh
    }
  }

  if (authToken) {
    setAuthorizationHeader(headers, authToken)
  }

  const response = await fetch(input, { ...init, headers })

  if (response.status === 401) {
    if (isRefreshRequest) {
      return finalizeResponse(response)
    }

    if (apiInstance.nextAuthAccessToken) {
      setAuthorizationHeader(headers, apiInstance.nextAuthAccessToken)
      const retryResponse = await fetch(input, { ...init, headers })
      if (retryResponse.status !== 401) {
        return finalizeResponse(retryResponse)
      }
    }

    if (isClient) {
      // Guest hitting a protected endpoint (no session cookies) — return 401
      // to the caller. Do not treat it as a lost session or bounce the SPA
      // (task cards must stay open to view for guests).
      const hadSession = Boolean(getAuthToken() || getRefreshToken())
      if (!hadSession && !isAuthenticated()) {
        // Unless the session died during this very request: the refresh above
        // dropped cookies the server had revoked. Looks identical to a guest
        // from here, but the store still says "logged in" and only this path
        // will ever correct it.
        if (startedWithSession) {
          await endSession()
        }
        return finalizeResponse(response)
      }

      if (!isRefreshing) {
        isRefreshing = true
        refreshPromise = refreshTokens().finally(() => {
          isRefreshing = false
          refreshPromise = null
        })
      }

      const accessToken = await refreshPromise

      if (accessToken) {
        setAuthorizationHeader(headers, accessToken)
        return finalizeResponse(await fetch(input, { ...init, headers }))
      }

      // A stale pre-login request can finish with 401 after the user has just
      // logged in. Do not wipe a still-valid refresh cookie on a transient
      // refresh failure.
      const refresh = getRefreshToken()
      if (!refresh || isTokenExpired(refresh) || !isAuthenticated()) {
        await endSession()
      }
    }
  }

  return finalizeResponse(response)
}

const marketApi = new MarketApi({
  baseUrl: process.env.NEXT_PUBLIC_API_URL,
  customFetch
})

const api = marketApi.api

export const apiRequest: MarketApi<unknown>['request'] =
  marketApi.request.bind(marketApi)

export default api
